import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

/**
 * Citation from an extraction
 */
interface ExtractionCitation {
  id: string;
  artifact_id: string;
  citation_id?: string | null;
  block_id?: string | null;
  transcript_line_id?: string | null;
  excerpt?: string | null;
  created_at: string;
  modified_at: string;
}

/**
 * Artifact extraction
 */
interface ArtifactExtraction {
  id: string;
  artifact_id?: string | null;
  created_at: string;
  modified_at: string;
  extraction_type: string;
  extraction: any;
  extra?: any;
  citations: ExtractionCitation[];
}

/**
 * Suggestion
 */
interface Suggestion {
  id: string;
  created_at: string;
  modified_at: string;
  status: string;
  provenance_status: string;
  action: string;
  body?: any;
  extraction_ids: string[];
  extra?: any;
  target_id?: string | null;
  extractions: ArtifactExtraction[];
}

/**
 * API client for fetching entity suggestions and extractions
 */
export const entitySuggestionsApiClient = createApi({
  reducerPath: 'entitySuggestionsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['EntitySuggestions'] as const,
  endpoints: (builder) => ({
    /**
     * Get suggestions for a specific entity (person, organization, vehicle, etc.)
     */
    getEntitySuggestions: builder.query<
      Suggestion[],
      {
        matterId: string;
        targetId: string;
      }
    >({
      query: ({ matterId, targetId }) =>
        `api/matters/${matterId}/suggestions/?target_id=${targetId}`,
      providesTags: (result, error, { targetId }) => [
        { type: 'EntitySuggestions', id: targetId },
      ],
    }),

    /**
     * Get suggestions for multiple entities at once
     */
    getBulkEntitySuggestions: builder.query<
      Record<string, Suggestion[]>,
      {
        matterId: string;
        targetIds: string[];
      }
    >({
      async queryFn({ matterId, targetIds }, _api, _extraOptions, baseQuery) {
        const results: Record<string, Suggestion[]> = {};

        // Fetch suggestions for each target ID in parallel
        await Promise.all(
          targetIds.map(async (targetId) => {
            const result = await baseQuery(
              `api/matters/${matterId}/suggestions/?target_id=${targetId}`,
            );
            if (result.data) {
              results[targetId] = result.data as Suggestion[];
            } else {
              results[targetId] = [];
            }
          }),
        );

        return { data: results };
      },
      providesTags: (result, error, { targetIds }) =>
        targetIds.map((id) => ({ type: 'EntitySuggestions' as const, id })),
    }),
  }),
});

export const {
  useGetEntitySuggestionsQuery,
  useGetBulkEntitySuggestionsQuery,
} = entitySuggestionsApiClient;
